import type { Chain } from 'viem';
import { useChains } from 'wagmi';

import { ChainSlug, Currency, tokensData } from '@/constants';
import {
  isBtcCurrency,
  isErc20Currency,
  isEvmNativeCurrency,
  decimalsForCurrency,
  isTronCurrency,
  isTrc20Currency,
  isLtcCurrency,
  isDogeCurrency,
  isCwebCurrency,
  isSolanaCurrency,
  isSplCurrency,
} from '@/utils';

import useTokenIconByCurrency from './useChainIconByCurrency';

export type ChainData = {
  chainSlug?: ChainSlug;
  isBtc: boolean;
  isLtc: boolean;
  isDoge: boolean;
  isEvm: boolean;
  isERC20: boolean;
  isNative: boolean;
  isTron: boolean;
  isCweb: boolean;
  isSolana: boolean;
  isPol: boolean;
  isL2: boolean;
  chainData: Chain | Partial<Chain> | null;
};

const DEFAULT_CHAIN_DATA: ChainData = {
  isNative: false,
  isBtc: false,
  isLtc: false,
  isDoge: false,
  isEvm: false,
  isERC20: false,
  isTron: false,
  isCweb: false,
  isSolana: false,
  isPol: false,
  isL2: false,
  chainData: null,
};

const useChainByCurrency = (currency?: Currency) => {
  const evmWagmiChains = useChains();
  const { getChainIcon } = useTokenIconByCurrency(currency);

  const collectChainData = (currency: Currency): ChainData => {
    if (isBtcCurrency(currency)) {
      return {
        ...DEFAULT_CHAIN_DATA,
        chainSlug: ChainSlug.BTC,
        isNative: true,
        isBtc: true,
        chainData: {
          name: 'Bitcoin',
          nativeCurrency: { name: 'Bitcoin', symbol: Currency.BTC, decimals: decimalsForCurrency(Currency.BTC) },
          custom: { icon: getChainIcon(Currency.BTC) },
        } as Partial<Chain>,
      };
    }

    if (isLtcCurrency(currency)) {
      return {
        ...DEFAULT_CHAIN_DATA,
        chainSlug: ChainSlug.LTC,
        isNative: true,
        isLtc: true,
        chainData: {
          name: 'Litecoin',
          nativeCurrency: { name: 'Litecoin', symbol: Currency.LTC, decimals: decimalsForCurrency(Currency.LTC) },
          custom: { icon: getChainIcon(Currency.LTC) },
        } as Partial<Chain>,
      };
    }
    if (isDogeCurrency(currency)) {
      return {
        ...DEFAULT_CHAIN_DATA,
        chainSlug: ChainSlug.DOGE,
        isNative: true,
        isDoge: true,
        chainData: {
          name: 'Dogecoin',
          nativeCurrency: { name: 'Dogecoin', symbol: Currency.DOGE, decimals: decimalsForCurrency(Currency.DOGE) },
          custom: { icon: getChainIcon(Currency.DOGE) },
        } as Partial<Chain>,
      };
    }

    if (isErc20Currency(currency)) {
      const { chainId } = tokensData[currency];

      if (chainId !== undefined) {
        const chainData = evmWagmiChains.find((chain) => chain.id === chainId);

        return {
          ...DEFAULT_CHAIN_DATA,
          chainSlug: ChainSlug.EVM,
          isEvm: true,
          isERC20: true,
          chainData: chainData || null,
        };
      }

      return DEFAULT_CHAIN_DATA;
    }

    if (isEvmNativeCurrency(currency)) {
      const { chainId } = tokensData[currency];

      if (chainId !== undefined) {
        const chainData = evmWagmiChains.find((chain) => chain.id === chainId);

        return {
          ...DEFAULT_CHAIN_DATA,
          chainSlug: ChainSlug.EVM,
          isEvm: true,
          isNative: true,
          chainData: chainData || null,
        };
      }

      return DEFAULT_CHAIN_DATA;
    }

    if (isTronCurrency(currency)) {
      return {
        ...DEFAULT_CHAIN_DATA,
        chainSlug: ChainSlug.TRON,
        isTron: true,
        isNative: true,
        chainData: {
          name: 'Tron',
          nativeCurrency: { name: 'Tron', symbol: Currency.TRX, decimals: decimalsForCurrency(Currency.TRX) },
          custom: { icon: getChainIcon(Currency.TRX) },
        } as Partial<Chain>,
      };
    }

    if (isTrc20Currency(currency)) {
      return {
        ...DEFAULT_CHAIN_DATA,
        chainSlug: ChainSlug.TRON,
        isTron: true,
        isNative: false,
        chainData: {
          name: 'Tron',
          nativeCurrency: { name: 'Tron', symbol: Currency.TRX, decimals: decimalsForCurrency(Currency.TRX) },
          custom: { icon: getChainIcon(Currency.TRX) },
        } as Partial<Chain>,
      };
    }

    if (isCwebCurrency(currency)) {
      return {
        ...DEFAULT_CHAIN_DATA,
        chainSlug: ChainSlug.CWEB,
        isCweb: true,
        isL2: true,
        isNative: true,
        chainData: {
          name: 'Coinweb',
          nativeCurrency: { name: 'Coinweb', symbol: Currency.CWEB, decimals: decimalsForCurrency(Currency.CWEB) },
          custom: { icon: getChainIcon(Currency.CWEB) },
        } as Partial<Chain>,
      };
    }

    if (isSolanaCurrency(currency)) {
      return {
        ...DEFAULT_CHAIN_DATA,
        chainSlug: ChainSlug.SOL,
        isSolana: true,
        isNative: true,
        chainData: {
          name: 'Solana',
          nativeCurrency: { name: 'Solana', symbol: Currency.SOL, decimals: decimalsForCurrency(Currency.SOL) },
          custom: { icon: getChainIcon(Currency.SOL) },
        } as Partial<Chain>,
      };
    }

    if (isSplCurrency(currency)) {
      return {
        ...DEFAULT_CHAIN_DATA,
        chainSlug: ChainSlug.SOL,
        isSolana: true,
        isNative: false,
        chainData: {
          name: 'Solana',
          nativeCurrency: { name: 'Solana', symbol: Currency.SOL, decimals: decimalsForCurrency(Currency.SOL) },
          custom: { icon: getChainIcon(Currency.SOL) },
        } as Partial<Chain>,
      };
    }

    return DEFAULT_CHAIN_DATA;
  };

  return {
    ...(currency && collectChainData(currency)),
    findChainByCurrency: collectChainData,
  };
};

export default useChainByCurrency;
